import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { TypedMemo } from '../../../common/types';
import { LockChip } from '../../components/LockedControl/LockedControl';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { xMessages } from '../xMessages';
import { xConnectLocked } from '../xPostsLimit';
import { platformMeta, platformSoftFill } from '../platformMeta';
import { SerializedSocialChannel, SocialPlatform } from '../types';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ComposerChannelChipsProps {
  /** All six platforms, connected or not. */
  channels: SerializedSocialChannel[];
  selected: SocialPlatform[];
  /** The platform whose handle form is open, if one is. */
  connecting?: SocialPlatform;
  /** Platforms this post's format cannot reach, such as a story on TikTok. */
  disabledPlatforms?: SocialPlatform[];
  /** Selected platforms that cannot post text only and have no media to post. */
  needsMediaPlatforms?: SocialPlatform[];
  /** Platforms that cannot take the attached media (or no media). They are grey, not red. */
  unavailablePlatforms?: SocialPlatform[];
  /** Unavailable platforms that take this media as a one-off publish from its asset card. */
  oneOffPlatforms?: SocialPlatform[];
  /** The X account used its API posts of this month and the post has a time in it (design c2). */
  isXMonthBlocked?: boolean;
  onToggle: (platform: SocialPlatform) => void;
  onConnect: (platform: SocialPlatform) => void;
}

function isConnected(channel: SerializedSocialChannel): boolean {
  return channel.handle !== null && channel.handle !== '';
}

/**
 * Where this post goes, one chip per platform.
 *
 * A platform with no handle cannot carry a post, so its chip asks for the
 * handle instead of selecting the platform — and it asks here, without leaving
 * the Composer. A platform the chosen format cannot reach says so and stays
 * out of the post.
 */
const ComposerChannelChipsComponent: React.FC<ComposerChannelChipsProps> = ({
  channels,
  selected,
  connecting,
  disabledPlatforms,
  needsMediaPlatforms,
  unavailablePlatforms,
  oneOffPlatforms,
  isXMonthBlocked = false,
  onToggle,
  onConnect,
}) => {
  const intl = useIntl();
  const { canUseX } = useMusic24Capabilities();
  const openUpgradePrompt = useUpgradePrompt();
  const openXPrompt = useCallback(() => openUpgradePrompt(SMLUpgradeFeature.x), [openUpgradePrompt]);

  const chips = useMemo(
    () =>
      channels.map((channel) => {
        const meta = platformMeta[channel.platform];
        const connected = isConnected(channel);
        const isStoryBlocked = disabledPlatforms?.includes(channel.platform) === true;
        // X with no handle on a plan without X: the lock opens the X prompt, not the handle form
        // (design c1). A saved handle makes X a normal manual chip on every plan.
        const isLocked = !connected && !isStoryBlocked && xConnectLocked(channel, canUseX);
        const isMonthBlocked = connected && !isStoryBlocked && channel.platform === 'x' && isXMonthBlocked;
        // A connected channel that cannot take this media waits for the right media.
        const isUnavailable = connected && !isStoryBlocked && unavailablePlatforms?.includes(channel.platform) === true;
        const isBlocked = isStoryBlocked || isUnavailable || isMonthBlocked;
        const isOn = connected && !isBlocked && selected.includes(channel.platform);
        const needsMedia = isOn && needsMediaPlatforms?.includes(channel.platform) === true;
        return (
          <button
            key={channel.platform}
            type="button"
            aria-pressed={isOn}
            disabled={isBlocked}
            className={clsx(
              styles.chip,
              isOn && styles.chipOn,
              !connected && styles.chipOff,
              isLocked && styles.chipLocked,
              isBlocked && styles.chipBlocked,
              needsMedia && styles.chipNeedsMedia,
              connecting === channel.platform && styles.chipConnecting
            )}
            onClick={() => {
              if (isLocked) {
                openXPrompt();
                return;
              }
              if (connected) {
                onToggle(channel.platform);
                return;
              }
              onConnect(channel.platform);
            }}
          >
            <span
              className={styles.chipMark}
              style={{ background: platformSoftFill(channel.platform), color: meta.color }}
            >
              <FontAwesomeIcon icon={meta.icon} />
            </span>
            <span className={styles.chipName}>{meta.name}</span>
            {isLocked ? (
              <>
                <FontAwesomeIcon icon={faLock} className={styles.chipLockIcon} />
                <LockChip feature={SMLUpgradeFeature.x} />
              </>
            ) : null}
            {isMonthBlocked ? (
              <span className={clsx(styles.chipConnect, styles.chipXMonthLimit)}>
                {intl.formatMessage(xMessages.chipMonthLimit)}
              </span>
            ) : null}
            {isUnavailable ? (
              <span className={styles.chipConnect}>
                {oneOffPlatforms?.includes(channel.platform) === true
                  ? intl.formatMessage({
                      id: 'app.sml.promote.composer.channels.unavailable.oneOff',
                      defaultMessage: '· publish it from the asset card',
                    })
                  : meta.mediaNeed === 'video'
                    ? intl.formatMessage({
                        id: 'app.sml.promote.composer.channels.unavailable.needsVideo',
                        defaultMessage: '· needs a video',
                      })
                    : intl.formatMessage({
                        id: 'app.sml.promote.composer.channels.unavailable.needsMedia',
                        defaultMessage: '· needs a photo or video',
                      })}
              </span>
            ) : null}
            {isStoryBlocked ? (
              <span className={styles.chipConnect}>
                {intl.formatMessage({
                  id: 'app.sml.promote.composer.channels.noStories',
                  defaultMessage: '· no stories',
                })}
              </span>
            ) : null}
            {needsMedia ? (
              <span className={styles.chipNeedsMediaNote}>
                {meta.mediaNeed === 'video'
                  ? intl.formatMessage({
                      id: 'app.sml.promote.composer.channels.needsVideo',
                      defaultMessage: '· needs a video',
                    })
                  : intl.formatMessage({
                      id: 'app.sml.promote.composer.channels.needsMedia',
                      defaultMessage: '· needs media',
                    })}
              </span>
            ) : null}
            {connected || isBlocked || isLocked ? null : (
              <span className={styles.chipConnect}>
                {intl.formatMessage({
                  id: 'app.sml.promote.composer.channels.connectHint',
                  defaultMessage: '· connect',
                })}
              </span>
            )}
          </button>
        );
      }),
    [
      canUseX,
      channels,
      connecting,
      disabledPlatforms,
      intl,
      isXMonthBlocked,
      needsMediaPlatforms,
      oneOffPlatforms,
      onConnect,
      onToggle,
      openXPrompt,
      selected,
      unavailablePlatforms,
    ]
  );

  return <div className={styles.chipRow}>{chips}</div>;
};

export const ComposerChannelChips = TypedMemo(ComposerChannelChipsComponent);
